const SHARE_PARAM = 'k';

export type ShareDecodeSuccess<T> = {ok: true; value: T};
export type ShareDecodeFailure = {ok: false};
export type ShareDecodeResult<T = unknown> = ShareDecodeSuccess<T> | ShareDecodeFailure;

const utf8Encoder = new TextEncoder();

function utf8ToBase64(text: string): string {
    const bytes = utf8Encoder.encode(text);
    let binary = '';
    for (let i = 0; i < bytes.length; i++) {
        binary += String.fromCharCode(bytes[i]);
    }
    return btoa(binary);
}

function binaryStringToBytes(binary: string): Uint8Array {
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i++) {
        bytes[i] = binary.charCodeAt(i);
    }
    return bytes;
}

function normalizeBase64(encoded: string): string {
    const compact = encoded.replace(/ /g, '+').replace(/\s+/g, '');
    const remainder = compact.length % 4;
    if (remainder === 0) return compact;
    // Length % 4 === 1 is not valid Base64; leave it so atob fails as a handled decode miss.
    if (remainder === 1) return compact;
    return compact + '='.repeat(4 - remainder);
}

function parseShareObject<T>(text: string): ShareDecodeResult<T> {
    try {
        const value: unknown = JSON.parse(text);
        if (value === null || typeof value !== 'object' || Array.isArray(value)) {
            return {ok: false};
        }
        return {ok: true, value: value as T};
    } catch {
        return {ok: false};
    }
}

function tryUtf8Decode(binary: string): string | null {
    try {
        return new TextDecoder('utf-8', {fatal: true}).decode(binaryStringToBytes(binary));
    } catch {
        return null;
    }
}

function parsePageUrl(pageUrl: string): URL | null {
    try {
        return new URL(pageUrl);
    } catch {
        return null;
    }
}

export function encodeShareState(state: unknown): string {
    const json = JSON.stringify(state);
    if (typeof json !== 'string') {
        throw new TypeError('Share state could not be serialized');
    }
    return utf8ToBase64(json);
}

export function decodeShareState<T = unknown>(encoded: unknown): ShareDecodeResult<T> {
    if (typeof encoded !== 'string' || encoded.trim() === '') {
        return {ok: false};
    }

    try {
        const binary = atob(normalizeBase64(encoded));
        const utf8 = tryUtf8Decode(binary);
        if (utf8 !== null) {
            const utf8Result = parseShareObject<T>(utf8);
            if (utf8Result.ok) return utf8Result;
        }
        // Legacy links used Latin-1 btoa(JSON.stringify(...)), which succeeds for ASCII and
        // characters like é but not for emoji. Keep that path so old José (no emoji) URLs restore.
        return parseShareObject<T>(binary);
    } catch {
        return {ok: false};
    }
}

export function readShareStateParam(pageUrl: string): string | null {
    const url = parsePageUrl(pageUrl);
    if (!url) return null;
    const values = url.searchParams.getAll(SHARE_PARAM).filter(value => value !== '');
    if (values.length === 0) return null;
    return values[values.length - 1];
}

export function decodeShareStateFromUrl<T = unknown>(pageUrl: string): ShareDecodeResult<T> {
    const encoded = readShareStateParam(pageUrl);
    if (encoded === null) return {ok: false};
    return decodeShareState<T>(encoded);
}

export function buildShareUrl(pageUrl: string, state: unknown): string {
    const encoded = encodeShareState(state);
    const url = new URL(pageUrl);
    url.searchParams.set(SHARE_PARAM, encoded);
    return url.toString();
}
